import FontFaceObserver from 'fontfaceobserver';

import { DEFAULT_TEXT_ELEMENT_COPY } from 'js/config/consts';
import config from 'js/config/config';
import { fontFilesManifest } from 'js/playback/helpers/fontFilesManifest';

import allFonts from '../resources/fonts';
import { defaultFontValue, fallbackFontValue } from '../resources/scribedefaults';

const variants = [
  {
    weight: 400,
    style: 'normal'
  },
  {
    weight: 400,
    style: 'italic'
  },
  {
    weight: 700,
    style: 'normal'
  },
  {
    weight: 700,
    style: 'italic'
  }
];

/*
  The way Google Fonts structures it's CSS files and font files, fonts subsets such as arabic characters don't automatically get downloaded
  via `webfontloader`. The font file containing subset characters will only get loaded once the DOM contains a character within that 
  subsets `unicode-range`. `fontfaceobserver` does a better job of loading the font file as you can provide it a test string - so we can build
  a test string from the text in the scribe to ensure that all the fonts needed to render the text in a scribe are preloaded.
*/
const loadFontsFiles = (testStringDict, includeTestText) => {
  return Promise.allSettled(
    Object.entries(testStringDict).flatMap(([font, testStr]) => {
      const variantPromises = variants.map(variant => {
        const obs = new FontFaceObserver(font, variant);
        const testString = includeTestText ? testStr : null;
        return obs.load(testString);
      });
      return variantPromises;
    })
  );
};

const VARIANT_DESCRIPTORS = [
  { key: 'Regular', weight: '400', style: 'normal' },
  { key: 'Bold', weight: '700', style: 'normal' },
  { key: 'Italic', weight: '400', style: 'italic' },
  { key: 'BoldItalic', weight: '700', style: 'italic' },
  { key: 'Web', weight: '400', style: 'normal' }
];

const loadFonts = testStringDict => {
  const cdnBase = config.BITMAP_FONT_WORKER_CDN;
  const loadPromises = Object.keys(testStringDict).flatMap(displayName => {
    const entry = fontFilesManifest.fonts.find(f => f.displayName === displayName);
    if (!entry) return [];

    return VARIANT_DESCRIPTORS.flatMap(({ key, weight, style }) => {
      const variant = entry[key];
      if (!variant) return [];

      const descriptor = { weight, style };
      if (variant.variable && variant.variableWeight !== undefined) {
        descriptor.variationSettings = `'wght' ${variant.variableWeight}`;
      }

      const face = new FontFace(displayName, `url(${cdnBase}/fonts/${variant.filename})`, descriptor);
      document.fonts.add(face);
      return [face.load()];
    });
  });

  return Promise.allSettled(loadPromises);
};

const addUserFontFiles = userFontsList => {
  return Promise.all(
    userFontsList.map(font => {
      const fontFace = new FontFace(font.fontname, `url(${font.fontUrl})`);
      document.fonts.add(fontFace);
      return fontFace.load();
    })
  );
};

async function preloadElementFonts(elements) {
  const allTextInElements = elements.reduce((str, element) => str + element.text, DEFAULT_TEXT_ELEMENT_COPY);
  const testStringDict = elements.reduce(
    (dict, element) => {
      const font = element.font.value;
      const str = element.text;
      return {
        ...dict,
        [font]: (dict[font] || '') + str
      };
    },
    {
      [defaultFontValue]: allTextInElements,
      [fallbackFontValue]: allTextInElements
    }
  );

  await loadFonts(testStringDict);
}

async function preloadAllScribesFonts(scribes) {
  const textElements = scribes.flatMap(scribe => scribe.elements).filter(el => el.type === 'Text' && !el.font.assetId);
  try {
    await preloadElementFonts(textElements);
  } catch (e) {
    console.error(e);
  }
}

export default async function preloadScribeFonts(scribe) {
  await preloadAllScribesFonts([scribe]);
}

export async function loadAllSelectableFonts(scribeText) {
  const familiesDict = allFonts.reduce((dict, font) => ({ ...dict, [font.value]: scribeText || font.value }), {});
  try {
    await loadFonts(familiesDict);
  } catch (e) {
    console.error(e);
  }
}

export async function loadUserSelectableFonts(scribeText, userFontsList) {
  try {
    await addUserFontFiles(userFontsList);
    const familiesDict = userFontsList.reduce((acc, current) => ({ ...acc, [current.fontname]: scribeText }), {});
    await loadFontsFiles(familiesDict, false);
  } catch (e) {
    console.error(e);
  }
}
